import { formatAddress, formatName, formatPhone } from './formatting'
import { format } from 'date-fns'
import foiAccessMethod from './foiAccessMethod'

const typeOfInformationLabels = {
  GENERAL: 'General information',
  PERSONAL: 'Your own personal information',
  THIRD_PARTY: 'Personal information about another individual'
}

function getContactInformation(foi) {
  const requestorContact = {
    Name: formatName(foi.requestor),
    Phone: foi.requestor.phone ? formatPhone(foi.requestor.phone) : '',
    Email: foi.requestor.email || '',
    Address: foi.requestor.address ? formatAddress(foi.requestor) : ''
  }

  if (foi.requestor.isBusiness) {
    return {
      'Business Details': {
        'Access for Business': foi.requestor.isBusiness ? 'Yes' : 'No',
        'Business Name': foi.requestor.businessName
      },
      'Your Details': requestorContact
    }
  }

  return {
    'Access for Business': foi.requestor.isBusiness ? 'Yes' : 'No',
    ...requestorContact
  }
}

function formatFOIDetails(foi = {}, accessMethodConfig) {
  const thirdPartyFiles = foi.thirdPartyFiles.reduce((acc, thirdPartyFile, index) => {
    acc[`Third Party File ${index}`] = thirdPartyFile
    return acc
  }, {})

  const formattedDetails = {
    contactInformation: getContactInformation(foi),
    typeOfInformation: {
      'Type of Information': foi.infoType.map(type => typeOfInformationLabels[type] || type).join('\n'),
      thirdPartyFiles
    },
    requestDetails: {
      'Time Period Start': foi.periodStart ? format(foi.periodStart, 'MMM-dd-yyyy') : '',
      'Time Period End': foi.periodEnd ? format(foi.periodEnd, 'MMM-dd-yyyy') : '',
      Request: foi.requestDescription || '',
      Addresses: foi.addresses.reduce((acc, address, index) => {
        acc[`Address ${index + 1}`] = formatAddress(address)
        acc[`Files ${index + 1}`] = foi.addresses[index].files
        return acc
      }, {}),
      'Files or Reference Numbers': { 'Files or Reference Numbers': foi.referenceNumbers || '' }
    },
    documentAccess: {
      'Access Method': foiAccessMethod.getSelectionLabel(foi.accessMethod, accessMethodConfig)
    }
  }

  return formattedDetails
}

// Parse date-only strings (YYYY-MM-DD) as local dates, not UTC
// to avoid timezone conversion issues
function parseDateOnly(dateStr) {
  if (!dateStr) return undefined
  // If it's a date-only string (YYYY-MM-DD), append local midnight time
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T00:00:00')
  }
  // Otherwise parse as-is (for ISO datetime strings with timezone)
  return new Date(dateStr)
}

function formatFoiDates(foi) {
  return {
    ...foi,
    createdAt: parseDateOnly(foi.createdAt),
    finalDate: parseDateOnly(foi.finalDate),
    ...(foi.closedDate && { closedDate: parseDateOnly(foi.closedDate) })
  }
}

function formatFOIForOrderSummary(foi = {}, accessMethodConfig) {
  const details = {
    'Requestor Name': formatName(foi.requestor),
    'Request for Business': foi.requestor.isBusiness ? 'Yes' : 'No',
    Contact: foi.requestor.email || '',
    'Access Method': foiAccessMethod.getSelectionLabel(foi.accessMethod, accessMethodConfig)
  }

  return details
}

function formatFOIForForm(foi = {}) {
  if (!foi || !foi.requestor) {
    return { contactInfo: undefined, requestInfo: undefined }
  }

  const contactInfo = {
    requestor: {
      isBusiness: foi.requestor.isBusiness || false,
      businessName: foi.requestor.businessName || '',
      firstName: foi.requestor.firstName || '',
      middleName: foi.requestor.middleName || '',
      lastName: foi.requestor.lastName || '',
      phone: foi.requestor.phone || '',
      email: foi.requestor.email || '',
      confirmEmail: foi.requestor.email || '',
      address: foi.requestor.address || '',
      city: foi.requestor.city || '',
      postalCode: foi.requestor.postalCode || '',
      province: foi.requestor.province || '',
      unit: foi.requestor.unit || ''
    }
  }

  const requestInfo = {
    infoType: foi.infoType || [],
    thirdPartyFiles: foi.thirdPartyFiles || [],
    hasAddresses: foi.addresses && foi.addresses.length > 0,
    addresses: foi.addresses || [],
    requestDescription: foi.requestDescription || '',
    accessMethod: foi.accessMethod || '',
    periodStart: foi.periodStart ? format(new Date(foi.periodStart), 'MMM-dd-yyyy') : undefined,
    periodEnd: foi.periodEnd ? format(new Date(foi.periodEnd), 'MMM-dd-yyyy') : undefined,
    referenceNumbers: foi.referenceNumbers || ''
  }

  return { contactInfo, requestInfo }
}

function formatFOIForSingleForm(foi) {
  const { contactInfo, requestInfo } = formatFOIForForm(foi)

  const formattedFoi = {
    ...requestInfo,
    ...contactInfo
  }
  formattedFoi.existingThirdPartyFiles = [...formattedFoi.thirdPartyFiles]
  formattedFoi.thirdPartyFiles = []
  formattedFoi.addresses = formattedFoi.addresses.map(a => {
    return {
      ...a,
      existingFiles: a.files,
      files: []
    }
  })

  return formattedFoi
}

const FINAL_RESPONSE_NOTE = 'Already attached to the final response'

function draftCommunicationNote(communicationName) {
  return `Already attached to draft communication "${communicationName}"`
}

function createLetterDraftCommunicationMap(communications, currentCommunicationId) {
  const result = communications.reduce((acc, curr) => {
    if (curr.letterId && curr.state === 'DRAFT' && curr.id !== currentCommunicationId) acc[curr.letterId] = curr.name
    return acc
  }, {})

  return result
}

function addUnavailableReasonToLetters(letters = [], { communications = [], finalResponseLetterId, currentCommunicationId } = {}) {
  const letterDraftCommunicationMap = createLetterDraftCommunicationMap(communications, currentCommunicationId)

  return letters.map(l => {
    if (finalResponseLetterId && l.id === finalResponseLetterId) return { ...l, unavailableReason: FINAL_RESPONSE_NOTE }
    if (letterDraftCommunicationMap[l.id]) return { ...l, unavailableReason: draftCommunicationNote(letterDraftCommunicationMap[l.id]) }
    return l
  })
}

export {
  formatFOIDetails,
  formatFoiDates,
  formatFOIForOrderSummary,
  formatFOIForForm,
  formatFOIForSingleForm,
  parseDateOnly,
  FINAL_RESPONSE_NOTE,
  draftCommunicationNote,
  addUnavailableReasonToLetters
}
